/* eslint no-underscore-dangle: 0 */
import axios, {
  AxiosError,
  AxiosInstance,
  AxiosRequestHeaders,
  AxiosResponse,
  InternalAxiosRequestConfig,
} from 'axios';

import {
  getAccessToken,
  getRefreshToken,
  getRefreshTries,
  getUserData,
  setRefreshTries,
  setTokens,
} from '~/services/localStorage/auth';
import ErrorCodes from '~/ui/pages/SignIn/models/ErrorCodes';
import forceLogout from '~/utils/auth/forceLogout';
import isAccessTokenValid from '~/utils/auth/isAccessTokenValid';
import refreshTokens from '~/utils/auth/refreshTokens';
import uuidv4 from '~/utils/auth/uuidv4';
import bugsnagClient from '~/utils/error/bugsnag';
import isRefreshTokenError from '~/utils/error/isRefreshTokenError';

const MAX_REFRESH_RETRIES = 3;

const formatErrorResponse = (err: any) => err?.response;

type IRequestCb = (token: string) => void;

let isRefreshing = false;
const refreshSubscribers: { [key: string]: IRequestCb } = {};
let lastFailedRefreshRequestId: string | null = null;

// add request to the queue while refreshing access token
const subscribeTokenRefresh = (cb: IRequestCb, url?: string) => {
  if (url) {
    refreshSubscribers[url] = cb;
  }
};

// update token in the requests queue
const onRefreshed = (token: string) => {
  Object.values(refreshSubscribers).map(cb => cb(token));
};

const handleLogout = async () => {
  await forceLogout();
};

const getTimezoneName = (): string => Intl.DateTimeFormat().resolvedOptions().timeZone;

const requestInterceptor = (instance: AxiosInstance, isPrivate: boolean) => {
  instance.interceptors.request.use(async (config: InternalAxiosRequestConfig) => {
    // Add timezone offset header to all requests
    if (!config.headers) {
      config.headers = {} as AxiosRequestHeaders;
    }
    config.headers.TimezoneName = getTimezoneName();

    if (!isPrivate) return config;

    let currentAccessToken = getAccessToken();
    const currentRefreshToken = getRefreshToken();

    const isTokenValid = isAccessTokenValid(currentAccessToken);

    if (!isTokenValid && !isRefreshing) {
      isRefreshing = true;
      const userData = getUserData();
      const requestId = lastFailedRefreshRequestId ?? uuidv4();

      try {
        await refreshTokens(
          {
            accessToken: currentAccessToken,
            refreshToken: currentRefreshToken,
            ...userData,
            requestId,
          },
          ({ accessToken, refreshToken }) => {
            setTokens({ accessToken, refreshToken });
            currentAccessToken = accessToken;
            lastFailedRefreshRequestId = null;
            setRefreshTries(0);
          },
        );
      } catch (e) {
        if (isRefreshTokenError(e)) {
          lastFailedRefreshRequestId = requestId; // Store requestId if refresh token was used

          const refreshTries = getRefreshTries();
          setRefreshTries((refreshTries || 0) + 1);
        }
        bugsnagClient.notify('refresh token before request error');
      } finally {
        isRefreshing = false;
      }
    }

    if (currentAccessToken) {
      config.headers.Authorization = `Bearer ${currentAccessToken}`;
    }

    return config;
  });
};

const privateFlow = async (instance: AxiosInstance, error: AxiosError) => {
  const currentAccessToken = getAccessToken();
  const currentRefreshToken = getRefreshToken();
  const userData = getUserData();
  const originalConfig = error.config;

  if (error.response?.status === 403) {
    bugsnagClient.notify('no access, 403, logout');
    // logout if user deactivate or try to access restricted content with missing permissions
    handleLogout();
  }

  if (error.response?.status === 401 && !(originalConfig as any)._retry) {
    // add request to the queue while refreshing access token
    const retryOrigReq = new Promise(resolve => {
      subscribeTokenRefresh((newToken: string) => {
        // replace the expired token and retry
        if (originalConfig.headers) {
          originalConfig.headers.Authorization = `Bearer ${newToken}`;
        }
        resolve(instance(originalConfig));
      }, originalConfig.url);
    });

    // perform token refresh
    if (!isRefreshing && currentAccessToken && currentRefreshToken) {
      (originalConfig as any)._retry = true; // set retry option
      isRefreshing = true;
      const requestId = lastFailedRefreshRequestId ?? uuidv4();
      try {
        await refreshTokens(
          {
            accessToken: currentAccessToken,
            refreshToken: currentRefreshToken,
            ...userData,
            requestId,
          },
          // Set new tokens
          // Request interceptor will get and use new updated `accessToken` from LocalStorage
          ({ accessToken, refreshToken }) => {
            setTokens({ accessToken, refreshToken });
            onRefreshed(accessToken);
            (originalConfig as any)._retry = false;
            lastFailedRefreshRequestId = null;
            setRefreshTries(0);
          },
        );
      } catch (err) {
        const refreshTries = getRefreshTries();
        if (isRefreshTokenError(err)) {
          lastFailedRefreshRequestId = requestId;
          setRefreshTries((refreshTries || 0) + 1);
        }
        if (refreshTries >= MAX_REFRESH_RETRIES || !isRefreshTokenError(err)) {
          bugsnagClient.notify('refresh token error, logout');
          handleLogout();
        }
      } finally {
        isRefreshing = false;
      }
    }

    return retryOrigReq;
  }

  const errorCode = (error as unknown as { data: { code: string } })?.data?.code as ErrorCodes;
  const shouldLogout =
    !currentAccessToken ||
    errorCode === ErrorCodes.NO_ROLE ||
    errorCode === ErrorCodes.REFRESH_TOKEN_INVALID;

  if (shouldLogout) {
    bugsnagClient.notify('refresh token invalid, no role, no accessToken, logout');
    return forceLogout();
  }

  return Promise.reject(formatErrorResponse(error));
};

const responseInterceptor = (instance: AxiosInstance, isPrivate: boolean) => {
  instance.interceptors.response.use(
    (response: AxiosResponse) => response,
    async (error: AxiosError) => {
      if (!isPrivate) {
        return Promise.reject(formatErrorResponse(error));
      }

      return privateFlow(instance, error);
    },
  );
};

const addInterceptors = (instance: AxiosInstance, isPrivate: boolean): void => {
  requestInterceptor(instance, isPrivate);
  responseInterceptor(instance, isPrivate);
};

const axiosInstance = (baseURL: string, isPrivate = false): AxiosInstance => {
  const instance = axios.create({
    baseURL,
    paramsSerializer: {
      indexes: null,
    },
  });
  addInterceptors(instance, isPrivate);
  return instance;
};

export default axiosInstance;
